import React, { useMemo } from 'react'

import clsx from 'clsx'
import {
  formatOrderDate,
  formatOrderTotal,
  getPaymentDisplayText,
} from 'lib/utils/orderUtils'

interface OrderPaymentDetailsProps {
  /**
   * The order object containing all order data
   */
  order: Order
  /**
   * Optional custom class name for the container
   */
  className?: string
  /**
   * Currency code for formatting (defaults to USD)
   */
  currency?: string
  /**
   * Locale for currency formatting (defaults to en-US)
   */
  locale?: string
  /**
   * Whether the user has permission to view payment details (admin or order owner)
   */
  canViewPaymentDetails?: boolean
}

const Divider = ({ className }: { className?: string }) => (
  <span
    className={clsx(
      'inline-flex text-base border-r border-neutral-400 mx-2 h-4 lg:mx-4',
      className,
    )}
  ></span>
)

const OrderPaymentDetails: React.FC<OrderPaymentDetailsProps> = ({
  order,
  className,
  currency = 'USD',
  locale = 'en-US',
  canViewPaymentDetails = false,
}) => {
  // Extract successful transaction from order
  const successfulTransaction = useMemo(() => {
    return order.transactions.find(
      (transaction) => transaction.state === 'successful',
    )
  }, [order.transactions])

  const paymentMethod = successfulTransaction?.payment_method

  const formattedDate = useMemo(() => {
    return formatOrderDate(order)
  }, [order])

  const formattedTotal = useMemo(() => {
    return formatOrderTotal(order, locale, currency)
  }, [order, locale, currency])

  if (!canViewPaymentDetails) {
    return null
  }

  return (
    <div className={className}>
      <div className="mt-2 type-body-5">
        {formattedDate}
        <Divider />
        {paymentMethod && <span>{getPaymentDisplayText(paymentMethod)}</span>}
      </div>
      <Divider className="md-max:hidden" />
      <span className="md-max:block md-max:mt-1">
        Total <span className="text-accessible-green">{formattedTotal}</span>
      </span>
    </div>
  )
}

export default OrderPaymentDetails
